/**
 * PDPA-compliant cookie banner, structured to extend to GDPR.
 *
 * Defaults to the most privacy-preserving option: nothing beyond strictly
 * necessary cookies is enabled until a choice is recorded, and no analytics
 * script is loaded before consent.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useContent } from '../i18n/LocaleProvider';
import { Button } from './ui-kit';

const KEY = 'usp-cookie-consent-v1';
const DEFAULT_PREFS = { necessary: true, analytics: false, marketing: false };
export const COOKIE_SETTINGS_EVENT = 'usp:cookie-settings';

function storedPrefs() {
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
}

export default function CookieConsent() {
  const { UI } = useContent();
  const copy = UI.cookies;
  const [visible, setVisible] = useState(false);
  const [managing, setManaging] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  useEffect(() => {
    const recorded = storedPrefs();
    // A Global Privacy Control signal is a standing instruction: honour it as a
    // rejection of everything non-essential rather than asking again.
    if (!recorded && navigator.globalPrivacyControl === true) {
      try {
        window.localStorage.setItem(
          KEY,
          JSON.stringify({ ...DEFAULT_PREFS, gpc: true, at: new Date().toISOString() })
        );
      } catch (err) {
        /* storage unavailable — the privacy-preserving default still applies */
      }
      return undefined;
    }
    if (!recorded) setVisible(true);

    const onSettings = () => {
      const current = storedPrefs();
      setPrefs({
        necessary: true,
        analytics: Boolean(current?.analytics),
        marketing: Boolean(current?.marketing),
      });
      setManaging(true);
      setVisible(true);
    };
    window.addEventListener(COOKIE_SETTINGS_EVENT, onSettings);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, onSettings);
  }, []);

  // The banner owns the bottom-right corner while it is up, so anything else
  // anchored there (the back-to-top control) stands down.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('has-consent-banner', visible);
    return () => root.classList.remove('has-consent-banner');
  }, [visible]);

  const persist = (value) => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify({ ...value, at: new Date().toISOString() }));
    } catch (err) {
      /* storage unavailable — the privacy-preserving default still applies */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (    <div
      className="cookie-banner"
      role="dialog"
      aria-label={copy.aria}
      data-testid="cookie-banner"
    >
      <p className="t-small">{copy.copy}</p>
      <p className="t-small" style={{ marginTop: 8 }}>
        <Link to="/cookie-policy">{copy.policy}</Link>
      </p>

      {managing ? (
        <div style={{ marginTop: 16, display: 'grid', gap: 12 }}>
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <input type="checkbox" checked disabled aria-label={copy.necessaryAria} />
            <span className="t-small">
              <strong>{copy.necessaryLabel}</strong> — {copy.necessaryNote}
            </span>
          </label>
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              checked={prefs.analytics}
              onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })}
            />
            <span className="t-small">
              <strong>{copy.analyticsLabel}</strong> — {copy.analyticsNote}
            </span>
          </label>
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <input
              type="checkbox"
              checked={prefs.marketing}
              onChange={(e) => setPrefs({ ...prefs, marketing: e.target.checked })}
            />
            <span className="t-small">
              <strong>{copy.marketingLabel}</strong> — {copy.marketingNote}
            </span>
          </label>
        </div>
      ) : null}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
        {managing ? (
          <Button variant="primary" onClick={() => persist(prefs)} data-testid="cookie-save">
            {copy.save}
          </Button>
        ) : (
          <Button variant="secondary" onClick={() => setManaging(true)} data-testid="cookie-manage">
            {copy.manage}
          </Button>
        )}
        <Button variant="secondary" onClick={() => persist(DEFAULT_PREFS)} data-testid="cookie-reject">
          {copy.reject}
        </Button>
        <Button
          variant="primary"
          onClick={() => persist({ necessary: true, analytics: true, marketing: true })}
          data-testid="cookie-accept"
        >
          {copy.accept}
        </Button>
      </div>
    </div>
  );
}
